import { useEffect, useRef, useState } from "react";
import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";

// Shared read-model hook for the console's read-only surfaces (Live Activity, Alert Queue,
// Rollout, …). Plain useEffect/useState — consistent with usePermissions/useUpdateControl, no
// extra cache layer. `load` is re-run when any value in `deps` changes or on reload(); pass every
// value `load` closes over in `deps`. Optional polling for live surfaces. Optional Realtime
// postgres_changes (account-filtered) triggers debounced reload; polling remains as fallback.
// Fails into an error state (never throws into render).

export type AsyncStatus = "loading" | "ready" | "error";

export interface UseReadModelRealtime {
  client: SupabaseClient;
  /** Unique channel name per surface + account (e.g. cad:activity:acct-id). */
  channel: string;
  /** Base table in supabase_realtime publication (not a view). */
  table: string;
  accountId: string;
  schema?: string;
}

export interface UseReadModelOptions {
  pollMs?: number;
  realtime?: UseReadModelRealtime;
}

export interface AsyncResult<T> {
  status: AsyncStatus;
  data: T | null;
  error: string | null;
  reload: () => void;
  /** Epoch ms of the last successful fetch (initial or reload). */
  lastRefreshedAt: number | null;
  /** True when the Realtime channel reports SUBSCRIBED. */
  realtimeConnected: boolean;
}

const REALTIME_DEBOUNCE_MS = 250;

function normalizeOptions(third?: number | UseReadModelOptions): UseReadModelOptions {
  if (typeof third === "number") {
    return { pollMs: third };
  }
  return third ?? {};
}

function subscribeAccountTable(
  client: SupabaseClient,
  config: UseReadModelRealtime,
  onChange: () => void,
  onStatus: (connected: boolean) => void,
): RealtimeChannel {
  const schema = config.schema ?? "public";
  const filter = `account_id=eq.${config.accountId}`;
  return client
    .channel(config.channel)
    .on(
      "postgres_changes",
      { event: "*", schema, table: config.table, filter },
      () => onChange(),
    )
    .subscribe((status) => {
      onStatus(status === "SUBSCRIBED");
    });
}

export function useReadModel<T>(
  load: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
  pollMsOrOptions?: number | UseReadModelOptions,
): AsyncResult<T> {
  const { pollMs, realtime } = normalizeOptions(pollMsOrOptions);
  const [status, setStatus] = useState<AsyncStatus>("loading");
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let active = true;

    async function run(): Promise<void> {
      try {
        const result = await load();
        if (!active) return;
        setData(result);
        setError(null);
        setStatus("ready");
        setLastRefreshedAt(Date.now());
      } catch (caught) {
        if (!active) return;
        setError(caught instanceof Error ? caught.message : "Failed to load");
        setStatus("error");
      }
    }

    void run();

    let intervalId: ReturnType<typeof setInterval> | undefined;
    if (pollMs !== undefined && pollMs > 0) {
      intervalId = setInterval(() => void run(), pollMs);
    }

    return () => {
      active = false;
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };
    // `load` is intentionally excluded; callers declare its inputs via `deps`.
  }, [...deps, nonce, pollMs]);

  useEffect(() => {
    if (realtime === undefined) {
      setRealtimeConnected(false);
      return;
    }

    function scheduleReload(): void {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        setNonce((value) => value + 1);
      }, REALTIME_DEBOUNCE_MS);
    }

    const channel = subscribeAccountTable(realtime.client, realtime, scheduleReload, setRealtimeConnected);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
      setRealtimeConnected(false);
      void realtime.client.removeChannel(channel);
    };
  }, [realtime?.channel, realtime?.table, realtime?.accountId, realtime?.schema, realtime?.client]);

  return {
    status,
    data,
    error,
    reload: () => setNonce((value) => value + 1),
    lastRefreshedAt,
    realtimeConnected,
  };
}
